import { Plus, RotateCw, Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Input } from "~/components/ui";
import type { OrganizationRow } from "~/features/admin/organizations/types";
import { OrganizationFormDialog } from "./OrganizationFormDialog";
import { OrganizationsTable } from "./OrganizationsTable";

interface OrganizationsPresenterProps {
  rows: OrganizationRow[];

  search: string;
  onSearchChange: (next: string) => void;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  onRefresh: () => void;
}

export function OrganizationsPresenter({
  rows,
  search,
  onSearchChange,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  onRefresh,
}: OrganizationsPresenterProps) {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <div className="mx-auto max-w-(--content-max) space-y-5 p-8">
      <div className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-dewey-title capitalize">{t("model.organization.modelNames")}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-3" />
              <Input
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={t("ui.search")}
                className="h-9 w-[240px] pl-8"
                aria-label={t("ui.search")}
              />
            </div>
            <Button type="button" variant="outline" size="icon" onClick={onRefresh} aria-label={t("ui.reload")}>
              <RotateCw />
            </Button>
            <Button type="button" size="sm" onClick={() => setCreateOpen(true)}>
              <Plus />
              <span>{t("ui.add")}</span>
            </Button>
          </div>
        </div>

        <OrganizationsTable
          rows={rows}
          emptyMessage={t("feature.organizations.empty")}
          globalFilter={search}
          onGlobalFilterChange={onSearchChange}
          pageIndex={pageIndex}
          onPageIndexChange={onPageIndexChange}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
        />
      </div>

      <OrganizationFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}
